import React, { useContext } from 'react';
import { useNavigate, useParams } from 'react-router';

import styled from '@emotion/styled';

import useExitDispatcher from '../../../../hooks/dashboard/driver-performance-report/useExitDispatcher';
import {
  ExitDispatcherDataType,
  ExitDispatcherInitialValues,
  ExitDispatcherSchema,
} from '../../../../models/dashboard/driver-performance-report/exit-dispatcher';
import { DASHBOARD } from '../../../../routes/routeConstants';
import { normalizeImageSrc } from '../../../../utils/utils';
import NotificationBanner from '../../../Banner/NotificationBanner';
import { FormSubmitButton } from '../../../Button/FormSubmitButton';
import { CustomDatePicker } from '../../../FormCom/CustomDatePicker';
import {
  CustomInlinePlainRadioFieldGroup,
  CustomPlainRadio,
} from '../../../FormCom/CustomInlinePlainRadioField';
import CustomSelectField from '../../../FormCom/CustomSelectField';
import { FormComponent } from '../../../FormCom/FormComponent';
import { TextAreaInputField } from '../../../FormCom/FormField';
import { TableContext } from '../../../Table/TableContext';
import ModalContainer from '../../ModalContainer';
import { ModalContext } from '../../ModalContext';
import PlainSuccessModal from '../../PlainSuccessModal';

interface ExitDispatcherModalProps {
  data: any;
}

const ExitDispatcherModal: React.FC<ExitDispatcherModalProps> = ({ data }) => {
  const { name: driverName, profilePicture: profilePictureParam } = useParams();
  const modalContext = useContext(ModalContext);
  const tableContext = useContext(TableContext);
  const { exitDispatcher, loading } = useExitDispatcher();
  const navigate = useNavigate();

  const driverProfilePicture =
    normalizeImageSrc(data?.profilePicture) ||
    normalizeImageSrc(profilePictureParam) ||
    '/static/img/profilePlaceholderLargePlain.png';

  const successActionFn = () => {
    modalContext?.setShowModal(false);
    navigate(`/${DASHBOARD}`);
  };

  const onSubmit = (value: ExitDispatcherDataType) => {
    const submissionData = {
      ...value,
      rehireEligibility: value.rehireEligibility === 'Yes' ? true : false,
    };
    exitDispatcher(submissionData, data?.driverId ?? '', () => {
      modalContext?.setShowSuccessComponent(true);
      tableContext?.setRefreshTable(!tableContext.refreshTable);
    });
  };

  return (
    <ModalContainer
      width={!modalContext?.showSuccessComponent ? '867px' : undefined}
      label='Exit Driver'
      successComponent={() => (
        <PlainSuccessModal
          successActionFn={successActionFn}
          description={`Driver ${driverName} has been successfully exited.`}
          // btnLabel='Go to Dashboard'
        />
      )}
      showSuccessComponent={modalContext?.showSuccessComponent}
    >
      <HeaderContainer>
        <Img src={driverProfilePicture} />
        <TextArea>
          <FirstTextRow>
            <BoldText>{driverName}</BoldText>
          </FirstTextRow>

          <SecondTextRow></SecondTextRow>
        </TextArea>
      </HeaderContainer>

      <NotificationBanner message='This action will permanently exit the driver from the company records and cannot be undone.' />

      <FormComponent
        initialValues={ExitDispatcherInitialValues}
        schema={ExitDispatcherSchema}
        onSubmit={onSubmit}
        className={'large-gap'}
      >
        <FormRow>
          <CustomDatePicker
            label='Exit Date'
            name='exitDate'
            id='exitDate'
            placeholder='Exit Date'
          />

          <CustomSelectField
            label='Reason for Exit'
            name='exitReason'
            id='exitReason'
            placeholder='Reason for Exit'
            optionData={['Voluntary Exit', 'End of contract', 'Termination']}
          />
        </FormRow>

        <FormRow>
          <CustomInlinePlainRadioFieldGroup
            name='rehireEligibility'
            label='Rehire Eligibility'
          >
            <CustomPlainRadio name='rehireEligibility' value='Yes'>
              Yes
            </CustomPlainRadio>
            <CustomPlainRadio name='rehireEligibility' value='No'>
              No
            </CustomPlainRadio>
          </CustomInlinePlainRadioFieldGroup>
          <div></div>
        </FormRow>

        <TextAreaInputField
          label='Final Review of Driver'
          name='exitComments'
          id='exitComments'
          type='text'
          placeholder='Write your final review of the driver at the workplace...'
        />

        <ButtonArea>
          <FormSubmitButton
            className='clear'
            onClick={() => modalContext?.setShowModal(false)}
            btnType='button'
          >
            Cancel
          </FormSubmitButton>
          <FormSubmitButton className='blue-solid' loading={loading}>
            Exit Driver
          </FormSubmitButton>
        </ButtonArea>
      </FormComponent>
    </ModalContainer>
  );
};

export default ExitDispatcherModal;

const HeaderContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  max-width: 827px;
  border-radius: 5px;

  gap: 24px;
`;

const TextArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Img = styled.img`
  width: 67px;
  height: 67px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid transparent;
`;

const BoldText = styled.span`
  font-weight: 600;
  font-size: 18px;
  line-height: 18px;
`;

const FirstTextRow = styled.div`
  display: flex;
  gap: 12px;
`;

const SecondTextRow = styled.div`
  display: flex;
  gap: 8px;
  color: ${({ theme }) => theme.palette.grayTextColor};
  font-size: 14px;
  line-height: 14px;
  font-weight: 400;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
`;

const ButtonArea = styled.div`
  display: flex;
  gap: 16px;
  margin-left: auto;
`;
